// packages/core/src/learning/school-modules/hard-money.ts
// ─────────────────────────────────────────────────────────────────────────
// Lens Pack for "Hard money". surface: 'school', homeSchool: 'hard-money'.
// module id 'school-hard-money' matches the exhibit's nativeModuleId.
//
// emoji + accentColor are PLACEHOLDERS (accentColor from SchoolMeta color
// #2c4a73) — flag for the presentation-tuning pass.
// tier/pillar: monetary worldview / purchasing-power mindset lens (SchoolMeta
// bucket 'macro-wealth-perspectives') → tier 'growth', pillar
// 'money-psychology-core' (per the protocol default: worldview/mindset lenses
// → growth / money-psychology-core). Same mapping as the sibling
// anti-fragility school. Flag for the presentation-tuning pass.
// ─────────────────────────────────────────────────────────────────────────

import type { LearningModule } from '../LearningTypes';

export const SCHOOL_HARD_MONEY_MODULE: LearningModule = {
	id: 'school-hard-money',
	title: 'Hard money',
	description:
		'The lens that thinks in purchasing power and monetary trust — taking inflation, scarcity, and dilution seriously, while holding the discipline-versus-flexibility tradeoff honestly instead of sliding into doom.',
	emoji: '🪙', // PLACEHOLDER — tune in presentation pass
	accentColor: '#2c4a73', // from SchoolMeta color — tune in presentation pass
	tier: 'growth',
	pillar: 'money-psychology-core',
	order: 0,
	surface: 'school',
	homeSchool: 'hard-money',
	lessons: [
		{
			id: 'school-hard-money-01',
			title: 'Money is trust under pressure',
			description:
				'Learn why Hard money cares about scarcity, discipline, and whether money keeps faith with savers.',
			pillar: 'money-psychology-core',
			schoolsOfThought: ['hard-money'],
			estimatedReadMinutes: 5,
			estimatedListenMinutes: 6,
			difficulty: 3,
			tags: ['monetary-trust', 'scarce-money', 'saver-protection'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: ['monetary trust', 'scarce money', 'saver protection'],
			blocks: [
				{
					type: 'paragraph',
					text: 'Money works because people trust it. You accept payment today because you believe it will still buy something tomorrow.',
				},
				{
					type: 'paragraph',
					text: 'Hard money believes that trust weakens when money can be created too easily. This lens is paying attention to scarcity, discipline, and whether savers are quietly diluted by decisions they did not make.',
				},
				{
					type: 'bullet_list',
					items: [
						'Money stores effort',
						'Scarcity supports trust',
						'Dilution changes incentives',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is thinking money is stable just because the number in the account does not move.',
				},
				{
					type: 'example',
					text: 'Someone saves $10,000 and feels secure. A year later, the number is still $10,000, but rent, food, insurance, and car costs are higher. The balance stayed the same, but the real power of the money changed.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is that hard money can become too rigid. A system with strict money discipline may protect savers, but it can also make crisis response harder and reduce flexibility when the economy is under stress.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this lens starts with your real picture. Look at your Dashboard and ask whether your plan depends on money holding its buying power. Then ask what happens if prices rise faster than income.',
				},
				{
					type: 'key_takeaway',
					text: 'Hard money sees money as stored trust, and trust weakens when purchasing power quietly leaks.',
				},
			],
			audioScript:
				'Hard money starts with a simple but serious idea: money is trust. You work today, accept dollars, save some of them, and believe those dollars will still mean something later. That belief is not automatic. It depends on the system. It depends on how money is created, how debt is handled, how inflation behaves, and how much people trust the currency.\n\nThis lens asks a question most people ignore until prices rise: what is my money actually worth over time? Not what is the number in the account. What can that number buy? That is the core difference. A balance can stay the same while real life gets more expensive. If rent rises, food rises, insurance rises, utilities rise, and wages do not keep up, then the same money buys less life.\n\nHard money believes scarcity matters because unlimited creation changes incentives. If money can be produced too easily, the hard money view says savers can be punished quietly. The person who delayed spending, worked extra, and saved cash may find that their stored effort buys less later. That feels unfair to this worldview. Hard money is trying to defend the saver, the worker, and the future buyer from hidden dilution.\n\nThink of someone who saves $10,000. They feel proud because that took discipline. Then a year or two later, the account still says $10,000, but the emergency fund covers fewer months of expenses. The car repair is more expensive. Groceries cost more. Rent moved up. The number did not go down, but the power did. Hard money notices that gap and refuses to brush it off.\n\nThe useful part of this lens is that it forces purchasing power thinking. It makes you ask better questions. Is my plan built on nominal dollars or real life costs? Does my runway still mean the same thing if prices rise? Am I confusing a stable balance with stable security? Those are good questions.\n\nBut the blind spot matters. Hard money can become too rigid. If every act of money creation is treated as corruption, the lens can miss why governments and central banks respond during wars, banking crises, recessions, or emergencies. Flexibility can be abused, but total rigidity can also create pain. A money system is not just a purity test. It has to function in messy human reality.\n\nInside Nisk, use this lens without becoming dramatic. Open your current picture. Look at your expenses. Ask what would happen if your basic costs rose by ten percent while income stayed flat. How much runway would you really have? What would feel weaker? What would need adjustment?\n\nHard money is at its best when it turns fear into clear thinking. It is not about predicting collapse. It is about respecting purchasing power.',
			flashcards: [
				{
					id: 'school-hard-money-01-fc-1',
					role: 'main_idea',
					front: 'What does hard money protect?',
					back: 'Monetary trust, scarcity, and purchasing power over time.',
				},
				{
					id: 'school-hard-money-01-fc-2',
					role: 'watch_out',
					front: 'What can numbers hide?',
					back: 'A balance can stay the same while buying power falls.',
				},
				{
					id: 'school-hard-money-01-fc-3',
					role: 'use_it',
					front: 'What should you check?',
					back: 'Whether your plan still works if basic costs rise.',
				},
			],
			applyAction: {
				type: 'openDashboard',
				label: 'Review your current picture',
			},
			revision: 1,
		},
		{
			id: 'school-hard-money-02',
			title: 'Inflation changes the deal',
			description:
				'Learn why Hard money treats inflation as a real life pressure, not just an economic headline.',
			pillar: 'money-psychology-core',
			schoolsOfThought: ['hard-money'],
			estimatedReadMinutes: 5,
			estimatedListenMinutes: 6,
			difficulty: 3,
			tags: [
				'purchasing-power-loss',
				'inflation-pressure',
				'real-cost-drift',
			],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: [
				'purchasing power loss',
				'inflation pressure',
				'real cost drift',
			],
			blocks: [
				{
					type: 'paragraph',
					text: 'Inflation is easy to ignore when it is small and steady. It becomes impossible to ignore when your normal life suddenly costs more.',
				},
				{
					type: 'paragraph',
					text: 'Hard money believes inflation changes the deal between today and tomorrow. If money buys less later, then saving, wages, rent, debt, and future plans all need a second look.',
				},
				{
					type: 'bullet_list',
					items: [
						'Prices change runway',
						'Wages may lag costs',
						'Savings can weaken quietly',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is treating inflation like a news topic instead of a household pressure.',
				},
				{
					type: 'example',
					text: 'A person builds three months of runway based on $3,000 per month of expenses. If those expenses rise to $3,450, the same emergency fund no longer protects three months. The plan did not fail. The price world changed around it.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is that inflation has many causes. Hard money can over simplify if it blames every price increase on money creation alone. Supply shocks, housing shortages, energy costs, wages, demand, and policy all matter too.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this lens becomes a stress test. Test what happens if expenses rise faster than income. The point is not panic. The point is to keep your plan honest.',
				},
				{
					type: 'key_takeaway',
					text: 'Inflation is not just higher prices. It is a quiet rewrite of what your plan can afford.',
				},
			],
			audioScript:
				'Inflation can sound abstract until it hits your grocery receipt, rent renewal, insurance bill, or car repair. Then it becomes personal fast. Hard money pays close attention to inflation because it sees inflation as a direct attack on purchasing power. Your money may look the same on paper, but if the cost of normal life rises, your money has less reach.\n\nThe hard money lens sees inflation as a broken promise between today and tomorrow. You saved dollars today because you expected those dollars to hold enough value later. If they buy less later, the deal changed. That affects emergency funds. It affects wages. It affects rent. It affects retirement plans. It affects debt. It affects how safe your cash feels. It affects how much pressure a family feels even when income has not technically dropped.\n\nPicture someone with $9,000 saved. Their monthly expenses used to be $3,000, so they felt like they had three months of runway. Then rent, food, utilities, and insurance rise. Now their monthly expenses are $3,450. Their $9,000 is still $9,000, but it no longer buys three months. The person did nothing wrong. The plan just got weaker because the cost world shifted.\n\nThat is why hard money thinks in purchasing power. It asks: how many months can this money buy? How much food, shelter, transportation, and safety can it cover? How much freedom does it actually create? This is a sharper question than just looking at account balances.\n\nThe lens is also skeptical of systems that create money too easily. It worries that when new money enters the system, the people closest to new credit or assets may benefit before normal workers feel relief. Meanwhile, people living on wages and savings may see costs rise faster than their income. That is the hard money concern in plain language.\n\nBut this lens can overreach. Inflation is complicated. Not every price increase comes from money creation alone. Housing shortages can raise rent. Energy shocks can raise transportation and food costs. Supply chain problems can raise goods prices. Corporate pricing power, wages, demand, interest rates, and policy choices can all matter. A balanced hard money lesson should not turn every price increase into one villain.\n\nInside Nisk, the practical move is to stress test your plan. What if expenses rise by $200 per month? What if rent jumps? What if insurance rises? What if income does not keep up? How does that change runway? How does it change your freedom date? How does it change the choices you can make?\n\nThe goal is not to become scared of every headline. The goal is to stop pretending purchasing power is automatic. Inflation changes the deal. Your plan should be honest enough to notice.',
			flashcards: [
				{
					id: 'school-hard-money-02-fc-1',
					role: 'main_idea',
					front: 'What does inflation change?',
					back: 'What your money can buy, including runway, savings, wages, and future plans.',
				},
				{
					id: 'school-hard-money-02-fc-2',
					role: 'watch_out',
					front: 'What is the inflation mistake?',
					back: 'Treating inflation like news instead of pressure on real household choices.',
				},
				{
					id: 'school-hard-money-02-fc-3',
					role: 'use_it',
					front: 'How do you apply it?',
					back: 'Stress test your plan with higher monthly costs.',
				},
			],
			applyAction: {
				type: 'openSimulator',
				label: 'Simulate higher expenses',
				scenarioType: 'custom',
			},
			revision: 1,
		},
		{
			id: 'school-hard-money-03',
			title: 'Discipline has a cost',
			description:
				'Learn why Hard money accepts monetary discipline, and why that discipline can also create tradeoffs.',
			pillar: 'money-psychology-core',
			schoolsOfThought: ['hard-money'],
			estimatedReadMinutes: 5,
			estimatedListenMinutes: 6,
			difficulty: 4,
			tags: [
				'monetary-discipline',
				'policy-rigidity',
				'crisis-flexibility',
			],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: [
				'monetary discipline',
				'policy rigidity',
				'crisis flexibility',
			],
			blocks: [
				{
					type: 'paragraph',
					text: 'Hard money likes discipline. It wants money to be harder to create, harder to manipulate, and harder to dilute.',
				},
				{
					type: 'paragraph',
					text: 'This lens believes strict money can protect savers and force governments, banks, and borrowers to face real limits. But strict systems also have costs when people need flexibility during shocks.',
				},
				{
					type: 'bullet_list',
					items: [
						'Discipline can protect trust',
						'Discipline can limit abuse',
						'Discipline can reduce emergency flexibility',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is acting like discipline has no downside.',
				},
				{
					type: 'example',
					text: 'A strict money system may make it harder to inflate away debts or fund promises casually. That can protect savers. But during a banking crisis, war, depression, or sudden collapse in demand, less flexibility may make pain harder to soften.',
				},
				{
					type: 'paragraph',
					text: 'The blind spot is purity. Hard money can become so focused on preventing abuse that it underweights unemployment, credit freezes, emergency response, and the human cost of tight money.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this lens should make you ask a balanced question: what does discipline protect, and what flexibility does it give up?',
				},
				{
					type: 'key_takeaway',
					text: 'Hard money protects against dilution, but rigidity can carry its own real cost.',
				},
			],
			audioScript:
				'Hard money is built around discipline. It wants money that cannot be created casually. It wants limits. It wants scarcity. It wants governments, banks, borrowers, and markets to face consequences instead of pushing costs into the future. That is the appeal. Discipline can protect trust.\n\nThe hard money view says loose money can hide problems. If too much borrowing, spending, or money creation is used to cover weak decisions, the system may look stable while pressure builds underneath. Savers may lose purchasing power. Asset prices may rise in ways that make normal life harder. Debt may grow because people believe someone else will soften the pain later. Hard money wants to stop that cycle by making money harder and limits clearer.\n\nThat is a real argument. If money can always be created to delay pain, discipline weakens. People may make promises they cannot keep. Governments may spend without clearly taxing. Borrowers may take risks because they expect rescue. Investors may chase assets because they expect money to stay loose. Hard money says this can distort behavior.\n\nBut discipline has a cost. That is where the lens needs humility. A strict money system may protect savers from dilution, but it may also reduce flexibility during emergencies. During a banking crisis, war, depression, pandemic, or sudden collapse in demand, the ability to respond can matter. If money is too rigid, the pain can fall hard on workers, borrowers, small businesses, and households with no buffer. A system that prevents abuse can also make recovery harder.\n\nThis is the core tradeoff. Hard money protects against one danger: too much discretion, too much dilution, too much debt, too much hidden cost. More flexible money protects against another danger: collapse, unemployment, frozen credit, and sudden crisis. Neither side gets to pretend the other risk is fake.\n\nA mature hard money lens does not say, “Discipline solves everything.” It says, “Discipline protects something important, but we need to know what it costs.” That is a much stronger view. It keeps the warning without turning it into ideology.\n\nInside Nisk, this matters because users need to understand worldviews as tradeoffs. If you are thinking about inflation, savings, or long term planning, hard money gives you a useful suspicion: is purchasing power being weakened? But if you are thinking about crisis response or recession, the same rigidity may become painful. The real question is not which side sounds tougher. The real question is what each system protects and what each system risks.\n\nFor your own life, the lesson is practical. Build discipline, but do not become brittle. Keep savings, but know that cash can lose power. Avoid over dependence on debt, but understand that flexibility can matter. Respect scarcity, but do not turn it into a religion.\n\nHard money is strongest when it teaches tradeoff, not purity.',
			flashcards: [
				{
					id: 'school-hard-money-03-fc-1',
					role: 'main_idea',
					front: 'What does hard money value?',
					back: 'Discipline, scarcity, and protection against monetary dilution.',
				},
				{
					id: 'school-hard-money-03-fc-2',
					role: 'watch_out',
					front: 'What can discipline cost?',
					back: 'It can reduce flexibility during crises and deepen short term pain.',
				},
				{
					id: 'school-hard-money-03-fc-3',
					role: 'use_it',
					front: 'What question should you ask?',
					back: 'What does this discipline protect, and what flexibility does it give up?',
				},
			],
			applyAction: {
				type: 'noToolAction',
				label: 'Name the tradeoff',
				prompt: 'Write what monetary discipline protects, then write what flexibility it might give up during a crisis.',
			},
			revision: 1,
		},
		{
			id: 'school-hard-money-04',
			title: 'Think in real buying power',
			description:
				'Learn why Hard money pushes users to compare account balances against what life actually costs.',
			pillar: 'money-psychology-core',
			schoolsOfThought: ['hard-money'],
			estimatedReadMinutes: 5,
			estimatedListenMinutes: 6,
			difficulty: 3,
			tags: ['real-buying-power', 'nominal-balance', 'cost-of-life'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: ['real buying power', 'nominal balance', 'cost of life'],
			blocks: [
				{
					type: 'paragraph',
					text: 'A dollar amount can feel clear until the world around it changes. The number is fixed. The life it buys is not.',
				},
				{
					type: 'paragraph',
					text: 'Hard money believes people should think in buying power. The question is not only how much do I have. The question is what can this money actually do for me if costs keep moving?',
				},
				{
					type: 'bullet_list',
					items: [
						'Runway is buying power',
						'Savings are buying power',
						'Income is buying power',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is feeling safer because the number grew, even if costs grew faster.',
				},
				{
					type: 'example',
					text: 'Someone gets a raise from $60,000 to $64,000, but rent, groceries, insurance, and gas rise faster than the raise. The income number improved, but real breathing room may not have.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is that real buying power can be hard to measure perfectly. Personal inflation is different for each person because their rent, commute, food, family needs, and location are different.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this lens belongs in Dashboard and runway checks. Translate numbers into months, choices, and pressure. That is how purchasing power becomes real.',
				},
				{
					type: 'key_takeaway',
					text: 'The number matters, but what the number can buy matters more.',
				},
			],
			audioScript:
				'Hard money keeps bringing you back to one question: what does the money buy? That question cuts through a lot of noise. It is easy to feel progress when account balances rise or income goes up. But if the cost of life rises faster, the progress may be smaller than it looks.\n\nThis is the difference between nominal numbers and real buying power. A nominal number is the number you see. A paycheck amount. A bank balance. A salary. A rent payment. Real buying power asks what that number can actually do. How many months of expenses does it cover? How much housing does it buy? How much food, transportation, insurance, health care, and freedom does it support?\n\nImagine someone gets a raise from $60,000 to $64,000. That sounds like progress, and it is not meaningless. But during the same period, rent goes up, groceries rise, insurance increases, and gas costs more. The person may technically earn more while feeling no more secure. Hard money notices this and says: do not celebrate the number until you compare it to the cost of life.\n\nThis lens is especially useful for runway. You might say you have $12,000 saved. Good. But the deeper question is how many months that buys. If monthly expenses are $3,000, that looks like four months. If expenses rise to $3,600, the same savings covers less. If a family obligation adds another cost, it changes again. The buying power of savings is personal.\n\nThat personal part matters. Inflation is not the same for everyone. A national number might say one thing, but your life may feel different. Renters feel rent changes. Drivers feel gas and insurance. Parents feel childcare. People with health needs feel medical costs. People in expensive cities feel housing pressure. Your personal inflation depends on your actual spending mix.\n\nThe hard money lens can get too dramatic if it treats every price change as a disaster. That is not helpful. But it is very useful when it makes you translate money into life. Instead of asking, “Do I have enough dollars?” ask, “Enough for what, at today’s costs, with some room for tomorrow’s costs?”\n\nInside Nisk, Dashboard can help ground this. Look at income, expenses, savings, and assumptions. Then use runway as a purchasing power check. How much time does your money buy? How many choices does it protect? How much stress does it reduce? If costs rise, what changes?\n\nThe point is not to predict inflation perfectly. You will not. The point is to avoid being fooled by clean numbers. A higher number can still buy less life. A stable balance can still become weaker. A good plan thinks in real buying power, not just account labels.',
			flashcards: [
				{
					id: 'school-hard-money-04-fc-1',
					role: 'main_idea',
					front: 'What is real buying power?',
					back: 'What your money can actually buy after costs and prices are considered.',
				},
				{
					id: 'school-hard-money-04-fc-2',
					role: 'watch_out',
					front: 'What can fool you?',
					back: 'A higher balance or income number when costs rise faster.',
				},
				{
					id: 'school-hard-money-04-fc-3',
					role: 'use_it',
					front: 'How do you check it?',
					back: 'Translate money into months, choices, pressure, and real expenses.',
				},
			],
			applyAction: {
				type: 'calculateRunway',
				label: 'Check real runway',
			},
			revision: 1,
		},
		{
			id: 'school-hard-money-05',
			title: 'Scarcity can become paranoia',
			description:
				'Learn where Hard money breaks down when distrust turns into fear, rigidity, or one story for every problem.',
			pillar: 'money-psychology-core',
			schoolsOfThought: ['hard-money'],
			estimatedReadMinutes: 5,
			estimatedListenMinutes: 6,
			difficulty: 4,
			tags: [
				'scarcity-mindset-risk',
				'monetary-paranoia',
				'balanced-skepticism',
			],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: [
				'scarcity mindset risk',
				'monetary paranoia',
				'balanced skepticism',
			],
			blocks: [
				{
					type: 'paragraph',
					text: 'Hard money is useful because it asks hard questions about trust. It breaks down when every answer becomes distrust.',
				},
				{
					type: 'paragraph',
					text: 'This lens can go wrong when scarcity turns into paranoia. If every policy is seen as theft, every institution as fake, and every future as collapse, the worldview stops teaching and starts trapping.',
				},
				{
					type: 'bullet_list',
					items: [
						'Skepticism is useful',
						'Doom thinking is costly',
						'One story cannot explain every economy',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is turning a real warning into a whole identity.',
				},
				{
					type: 'example',
					text: 'Someone becomes so focused on currency fear that they ignore skill building, income growth, health, relationships, and practical planning. They may understand one risk better, but their life becomes less balanced.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is that trust should not be blind. Hard money is right to question dilution and purchasing power. The fix is not to dismiss the concern. The fix is to hold it with other truths.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this lens should end with balance. Ask what purchasing power risk means for your real plan, then keep building income, skills, runway, relationships, and options.',
				},
				{
					type: 'key_takeaway',
					text: 'Hard money should sharpen your judgment, not turn uncertainty into permanent fear.',
				},
			],
			audioScript:
				'Hard money is useful because it asks questions people should ask. Can money be diluted? Can savings lose power? Can debt grow too large? Can governments and institutions make choices that quietly shift costs onto normal people? Can inflation change the life that money buys? These are serious questions. Ignoring them is not wisdom.\n\nBut hard money can break down when skepticism becomes paranoia. That is the danger. A person can start with a healthy concern about purchasing power and end up seeing every institution as fake, every policy as theft, every currency as doomed, and every future as collapse. At that point, the lens stops helping. It becomes a trap.\n\nThe problem with doom thinking is that it narrows life. If someone becomes obsessed with one risk, they may ignore other parts of resilience. They might ignore income growth. They might ignore skills. They might ignore health. They might ignore relationships. They might ignore career options. They might ignore the practical reality that bills still need to be paid in the current system. They might spend more time predicting disaster than building capacity.\n\nThat does not mean the concern is fake. Purchasing power risk is real. Inflation can hurt. Currency systems can be mismanaged. Debt can create pressure. Savers can be punished in subtle ways. The hard money lens sees something important. But no single lens explains every economic problem. Housing shortages, technology, demographics, energy costs, wages, policy, supply chains, war, culture, and market behavior all matter too. The world is bigger than one story.\n\nA mature hard money view says: be skeptical, but stay functional. Think about purchasing power, but do not stop building skills. Respect scarcity, but do not turn it into fear of all flexibility. Question debt, but do not assume every use of credit is moral failure. Watch inflation, but do not let headlines run your nervous system. Protect your savings, but also protect your ability to earn, adapt, and make clear decisions.\n\nInside Nisk, this is exactly why schools are lenses, not tribes. Hard money helps you see monetary risk. It does not need to become your entire identity. Use it to ask better questions. What happens if costs rise? What happens if cash loses buying power? How much runway do I really have? Am I too dependent on one system? Then balance that with other lenses. Income expansion asks how you grow earning power. Risk management asks what downside you can survive. Life design asks what kind of life the money is meant to support.\n\nThe point is not to be fearless. The point is to be clear. Hard money should sharpen your judgment, not trap you in permanent fear.',
			flashcards: [
				{
					id: 'school-hard-money-05-fc-1',
					role: 'main_idea',
					front: 'Where can hard money break?',
					back: 'When healthy skepticism turns into fear, rigidity, or one story for everything.',
				},
				{
					id: 'school-hard-money-05-fc-2',
					role: 'watch_out',
					front: 'What does doom thinking cost?',
					back: 'It can distract from income, skills, health, relationships, and practical action.',
				},
				{
					id: 'school-hard-money-05-fc-3',
					role: 'use_it',
					front: 'What is the balanced move?',
					back: 'Respect purchasing power risk while still building options in real life.',
				},
			],
			applyAction: {
				type: 'noToolAction',
				label: 'Balance the lens',
				prompt: 'Write one hard money concern that matters, then write three practical actions that still build your real life.',
			},
			revision: 1,
		},
	],
};
